/**
 * The Journal repository: F-JP-18 through F-JP-27 and F-JP-30, assembled.
 *
 * Everything genuinely difficult lives in the modules beside this one — the
 * decode pipeline in `codec.ts`, the twelve-step write in `verifiedWrite.ts`,
 * the staging bundle in `deletion.ts`, the move rules in `quarantine.ts`, the
 * structural contract in `validation.ts`. What is left here is the part that has
 * to be read as one story: availability before every operation, quarantine
 * before every refusal, and the fact that a value handed back to the app has
 * *always* been read out of storage again.
 *
 * Two things this class deliberately does not have:
 *
 *  - **A cached availability answer.** Storage can stop resolving while the app
 *    is running, and a write into a repository that has gone away is how an
 *    empty library gets written over a full one (F-JP-19). The check is cheap
 *    and it runs every time.
 *  - **A source of non-determinism.** The clock and the bundle/token names
 *    arrive as a `now()` dependency. `@ff/store` may not reach for a platform
 *    global, and staging filenames are the only thing in this package that
 *    needs one.
 */

import type {
  JournalAttachment,
  JournalDraft,
  JournalDraftId,
  JournalEntry,
  JournalEntryId,
  JournalGoal,
  JournalGoalId,
  JournalItemLoad,
  JournalLibraryRead,
  JournalRecordType,
  JournalRecoveryItem,
  JournalRepositoryErrorCode,
  Sha256Hex,
} from "@ff/domain";
import { JOURNAL_REVISION_MAX } from "@ff/domain";
import type {
  DigestPort,
  JournalAvailability,
  JournalDeletionResult,
  JournalRecoveryExport,
  JournalRepositoryPort,
  JournalWriteResult,
} from "@ff/domain/ports";

import { canonicalJson } from "../kernel/canonicalJson";
import type { JournalAttachmentVerifier } from "./attachments";
import {
  createJournalAttachmentVerifier,
  journalDigestChunks,
  verifyJournalAttachments,
} from "./attachments";
import type { JournalRecordTypes } from "./codec";
import {
  decodeJournalRecordBytes,
  encodeJournalRecord,
  journalRecordIsMovable,
} from "./codec";
import type { JournalDeletionRequest } from "./deletion";
import { recoverJournalDeletions, runJournalDeletion } from "./deletion";
import type { JournalFileStore } from "./files";
import { JournalFileError, journalPathJoin } from "./files";
import {
  JOURNAL_DIRECTORIES,
  JOURNAL_STAGED_ATTACHMENTS,
  isApprovedJournalRecoveryPath,
  journalAttachmentDirectory,
  journalAttachmentPath,
  journalLiveDirectory,
  journalQuarantineDirectory,
  journalQuarantineRecordPath,
  journalRecordIdFromFileName,
  journalRecordPath,
  journalStagedAttachmentName,
  journalStagedRecordName,
} from "./layout";
import { quarantineJournalRecord } from "./quarantine";
import { journalVerifiedWrite } from "./verifiedWrite";
import { decodeJournalEntry, decodeJournalDraft, decodeJournalGoal } from "./validation";

/* ------------------------------------------------------------------------- *
 * Construction
 * ------------------------------------------------------------------------- */

export interface JournalRepositoryDependencies {
  readonly files: JournalFileStore;
  readonly digest: DigestPort;
  /** Epoch milliseconds. Used only to name staging files uniquely. */
  readonly now: () => number;
  /**
   * Replaces the built-in integrity check. P18's `JournalAttachmentPort.verify`
   * drops straight in; until then the repository verifies attachments itself,
   * because F-JP-28 says the check runs before *every* entry and draft write
   * and a write path with an optional integrity check has no integrity check.
   */
  readonly verifyAttachment?: JournalAttachmentVerifier;
}

export function createJournalRepository(
  dependencies: JournalRepositoryDependencies,
): JournalRepository {
  return new JournalRepository(dependencies);
}

/* ------------------------------------------------------------------------- *
 * Internal shapes
 * ------------------------------------------------------------------------- */

interface ScanResult<T> {
  readonly items: readonly T[];
  readonly recoveries: readonly JournalRecoveryItem[];
}

type RecordLoad<T> =
  | { readonly kind: "loaded"; readonly value: T }
  | { readonly kind: "missing" }
  | { readonly kind: "recovery"; readonly item: JournalRecoveryItem };

const RECORD_TYPES: readonly JournalRecordType[] = ["entry", "draft", "goal"];

/** A repository error, whatever the substrate called it. */
function errorCodeOf(error: unknown): JournalRepositoryErrorCode {
  // "Non-repository failures normalize to repositoryUnavailable" (F-JP-19):
  // an error this package did not raise says nothing about which step failed,
  // and guessing a specific code would put a wrong sentence in front of a user.
  return error instanceof JournalFileError ? error.code : "repositoryUnavailable";
}

/* ------------------------------------------------------------------------- *
 * The repository
 * ------------------------------------------------------------------------- */

export class JournalRepository implements JournalRepositoryPort {
  readonly #files: JournalFileStore;
  readonly #digest: DigestPort;
  readonly #now: () => number;
  readonly #verifyAttachment: JournalAttachmentVerifier;
  #sequence = 0;

  constructor(dependencies: JournalRepositoryDependencies) {
    this.#files = dependencies.files;
    this.#digest = dependencies.digest;
    this.#now = dependencies.now;
    this.#verifyAttachment =
      dependencies.verifyAttachment ??
      createJournalAttachmentVerifier(dependencies.files, dependencies.digest);
  }

  /* --- availability ------------------------------------------------------ */

  async availability(): Promise<JournalAvailability> {
    const error = await this.#ensureAvailable();
    return error === undefined ? { status: "available" } : { status: "unavailable", error };
  }

  /* --- entries ----------------------------------------------------------- */

  async readEntries(): Promise<JournalLibraryRead<JournalEntry>> {
    return this.#readLibrary("entry");
  }

  async readEntry(id: JournalEntryId): Promise<JournalItemLoad<JournalEntry>> {
    return this.#readItem("entry", id);
  }

  async saveEntry(entry: JournalEntry): Promise<JournalWriteResult<JournalEntry>> {
    const unavailable = await this.#ensureAvailable();
    if (unavailable !== undefined) return { kind: "failed", error: unavailable };

    const validated = decodeJournalEntry(entry, "write");
    if (!validated.ok) {
      // There is no `invalidContent` in `JournalRepositoryErrorCode`, and
      // `writeNotVerified` is the honest neighbour: nothing was written, the
      // durable record is untouched, and the value could not be certified.
      return { kind: "failed", error: "writeNotVerified" };
    }
    const candidate = validated.value;

    const current = await this.#loadForWrite("entry", candidate.id);
    if (current.kind === "recovery") return { kind: "failed", error: "recoveryRequired" };
    if (current.kind === "failed") return { kind: "failed", error: current.error };

    const existing = current.value;
    if (existing !== undefined && canonicalJson(existing) === canonicalJson(candidate)) {
      // Identical short-circuit: re-writing the same bytes would spend a
      // backup, two verification reads and a revision step to change nothing.
      return { kind: "written", value: existing };
    }

    const conflict = revisionConflict(existing, candidate);
    if (conflict !== undefined) return { kind: "failed", error: conflict };

    const attachmentError = await verifyJournalAttachments(
      this.#verifyAttachment,
      candidate.attachments,
    );
    if (attachmentError !== undefined) return { kind: "failed", error: attachmentError };

    return this.#writeRecord("entry", candidate.id, candidate);
  }

  async deleteEntry(id: JournalEntryId): Promise<JournalDeletionResult> {
    const unavailable = await this.#ensureAvailable();
    if (unavailable !== undefined) return { kind: "failed", error: unavailable };

    // Refuses anything in a recovery state (F-JP-25): the durable truth for this
    // id is unknown, and a transaction cannot promise to remove a record it
    // cannot read.
    for (const type of ["entry", "draft"] as const) {
      const blocked = await this.#inRecovery(type, id);
      if (blocked) return { kind: "failed", error: "recoveryRequired" };
    }

    return this.#runDeletion([
      { kind: "file", path: journalRecordPath("entry", id), stagedName: journalStagedRecordName("entry") },
      { kind: "file", path: journalRecordPath("draft", id), stagedName: journalStagedRecordName("draft") },
      { kind: "directory", path: journalAttachmentDirectory(id), stagedName: JOURNAL_STAGED_ATTACHMENTS },
    ]);
  }

  /* --- drafts ------------------------------------------------------------ */

  async readDrafts(): Promise<JournalLibraryRead<JournalDraft>> {
    return this.#readLibrary("draft");
  }

  async readDraft(id: JournalDraftId): Promise<JournalItemLoad<JournalDraft>> {
    return this.#readItem("draft", id);
  }

  async saveDraft(draft: JournalDraft): Promise<JournalWriteResult<JournalDraft>> {
    const unavailable = await this.#ensureAvailable();
    if (unavailable !== undefined) return { kind: "failed", error: unavailable };

    if (draft.id !== draft.entry.id) {
      // A draft's id is its entry's id by construction; a pair that disagrees
      // would make promotion an id reconciliation instead of a transaction.
      return { kind: "failed", error: "immutableContextChanged" };
    }

    const validated = decodeJournalDraft(draft, "write");
    if (!validated.ok) return { kind: "failed", error: "writeNotVerified" };
    const candidate = validated.value;

    const current = await this.#loadForWrite("draft", candidate.id);
    if (current.kind === "recovery") return { kind: "failed", error: "recoveryRequired" };
    if (current.kind === "failed") return { kind: "failed", error: current.error };

    const existing = current.value;
    if (existing !== undefined && canonicalJson(existing) === canonicalJson(candidate)) {
      return { kind: "written", value: existing };
    }
    if (existing !== undefined && candidate.savedAt < existing.savedAt) {
      // Drafts order by timestamp, not revision: a save that moves `savedAt`
      // backwards is an older autosave arriving late.
      return { kind: "failed", error: "revisionConflict" };
    }

    const attachmentError = await verifyJournalAttachments(
      this.#verifyAttachment,
      candidate.entry.attachments,
    );
    if (attachmentError !== undefined) return { kind: "failed", error: attachmentError };

    return this.#writeRecord("draft", candidate.id, candidate);
  }

  async promoteDraft(draft: JournalDraft): Promise<JournalWriteResult<JournalEntry>> {
    const saved = await this.saveEntry(draft.entry);
    if (saved.kind === "failed") return saved;

    // Only the draft record is removed — no attachment-ownership audit, because
    // the entry that just verified owns every attachment the draft did.
    //
    // A failure here is deliberately not reported as a failed promotion. The
    // entry is durable, and the leftover draft holds the identical entry, so the
    // draft-overlaid projection shows exactly the same content until the next
    // pass clears it. Answering "failed" would tell the user their writing was
    // not saved when it was.
    await this.#runDeletion([
      { kind: "file", path: journalRecordPath("draft", draft.id), stagedName: journalStagedRecordName("draft") },
    ]);
    return saved;
  }

  async discardDraft(id: JournalDraftId): Promise<JournalDeletionResult> {
    const unavailable = await this.#ensureAvailable();
    if (unavailable !== undefined) return { kind: "failed", error: unavailable };

    let entries: ScanResult<JournalEntry>;
    let drafts: ScanResult<JournalDraft>;
    try {
      entries = await this.#scan("entry");
      drafts = await this.#scan("draft");
    } catch (error) {
      return { kind: "failed", error: errorCodeOf(error) };
    }

    if (entries.recoveries.length > 0 || drafts.recoveries.length > 0) {
      // Ownership of this draft's media cannot be proven while any record in
      // either library is unreadable. The documented bias is to prefer orphaned
      // bytes over deleting media a durable entry may still reference.
      return { kind: "failed", error: "recoveryRequired" };
    }

    const draft = drafts.items.find((candidate) => candidate.id === id);
    if (draft === undefined) return { kind: "nothingToDelete" };

    const referenced = new Set<string>();
    for (const entry of entries.items) {
      for (const attachment of entry.attachments) referenced.add(attachment.id);
    }
    for (const other of drafts.items) {
      if (other.id === id) continue;
      for (const attachment of other.entry.attachments) referenced.add(attachment.id);
    }

    const exclusive = draft.entry.attachments
      .filter((attachment) => !referenced.has(attachment.id))
      // Ascending uuid order, so a partial failure stops at the same file every
      // run and the bundle a recovery pass finds is reproducible.
      .sort((left, right) => (left.id < right.id ? -1 : left.id > right.id ? 1 : 0));

    const targets: JournalDeletionRequest[] = [
      { kind: "file", path: journalRecordPath("draft", id), stagedName: journalStagedRecordName("draft") },
      ...exclusive.map((attachment: JournalAttachment) => ({
        kind: "file" as const,
        path: journalAttachmentPath(attachment.ownerEntryID, attachment.id),
        stagedName: journalStagedAttachmentName(attachment.id),
      })),
    ];
    return this.#runDeletion(targets);
  }

  /* --- goals ------------------------------------------------------------- */

  async readGoals(): Promise<JournalLibraryRead<JournalGoal>> {
    return this.#readLibrary("goal");
  }

  async readGoal(id: JournalGoalId): Promise<JournalItemLoad<JournalGoal>> {
    return this.#readItem("goal", id);
  }

  async saveGoal(goal: JournalGoal): Promise<JournalWriteResult<JournalGoal>> {
    const unavailable = await this.#ensureAvailable();
    if (unavailable !== undefined) return { kind: "failed", error: unavailable };

    const validated = decodeJournalGoal(goal);
    if (!validated.ok) return { kind: "failed", error: "writeNotVerified" };
    const candidate = validated.value;

    const current = await this.#loadForWrite("goal", candidate.id);
    if (current.kind === "recovery") return { kind: "failed", error: "recoveryRequired" };
    if (current.kind === "failed") return { kind: "failed", error: current.error };

    const existing = current.value;
    if (existing !== undefined && canonicalJson(existing) === canonicalJson(candidate)) {
      return { kind: "written", value: existing };
    }
    return this.#writeRecord("goal", candidate.id, candidate);
  }

  async deleteGoal(id: JournalGoalId): Promise<JournalDeletionResult> {
    const unavailable = await this.#ensureAvailable();
    if (unavailable !== undefined) return { kind: "failed", error: unavailable };
    if (await this.#inRecovery("goal", id)) {
      return { kind: "failed", error: "recoveryRequired" };
    }
    return this.#runDeletion([
      { kind: "file", path: journalRecordPath("goal", id), stagedName: journalStagedRecordName("goal") },
    ]);
  }

  /* --- recovery ---------------------------------------------------------- */

  async recoveries(): Promise<readonly JournalRecoveryItem[]> {
    if ((await this.#ensureAvailable()) !== undefined) return [];
    const found: JournalRecoveryItem[] = [];
    for (const type of RECORD_TYPES) {
      try {
        found.push(...(await this.#scan(type)).recoveries);
      } catch {
        // One library that will not enumerate must not hide the other two's
        // preserved records — that list is how the user gets them back.
        continue;
      }
    }
    return found;
  }

  async exportRecovery(item: JournalRecoveryItem): Promise<JournalRecoveryExport> {
    const unavailable = await this.#ensureAvailable();
    if (unavailable !== undefined) return { kind: "rejected", error: unavailable };

    if (!isApprovedJournalRecoveryPath(item.path)) {
      return { kind: "rejected", error: "unsafeRecoveryPath" };
    }

    let read;
    try {
      read = await this.#files.read(item.path);
    } catch (error) {
      return { kind: "rejected", error: errorCodeOf(error) };
    }
    if (read.kind !== "bytes") return { kind: "rejected", error: "unsafeRecoveryPath" };

    // Re-verified at the boundary: the row's digest has to still describe the
    // bytes on disk, or the copy about to be shared is not the preserved record.
    if (read.bytes.byteLength !== item.byteCount) {
      return { kind: "rejected", error: "unsafeRecoveryPath" };
    }
    if ((await this.#hash(read.bytes)) !== item.contentSHA256) {
      return { kind: "rejected", error: "unsafeRecoveryPath" };
    }
    return { kind: "verified", item, bytes: read.bytes };
  }

  /* --- internals: availability ------------------------------------------- */

  async #ensureAvailable(): Promise<JournalRepositoryErrorCode | undefined> {
    try {
      for (const directory of JOURNAL_DIRECTORIES) {
        await this.#files.createDirectory(directory);
        if (!(await this.#files.directoryExists(directory))) {
          return "repositoryUnavailable";
        }
        // Enumerating is the check that catches a directory that resolves but
        // will not list — the fail-closed trigger F-JP-19 names.
        await this.#files.list(directory);
      }
      await recoverJournalDeletions(this.#files);
    } catch (error) {
      return errorCodeOf(error);
    }
    return undefined;
  }

  /* --- internals: reading ------------------------------------------------ */

  async #readLibrary<K extends JournalRecordType>(
    type: K,
  ): Promise<JournalLibraryRead<JournalRecordTypes[K]>> {
    const unavailable = await this.#ensureAvailable();
    if (unavailable !== undefined) {
      // Never an empty list standing in for failure: an empty Journal and an
      // unreachable one are different facts and only one of them is safe to
      // write into.
      return { items: [], recoveries: [], availabilityError: unavailable };
    }
    try {
      const scan = await this.#scan(type);
      return { items: scan.items, recoveries: scan.recoveries };
    } catch (error) {
      return { items: [], recoveries: [], availabilityError: errorCodeOf(error) };
    }
  }

  async #readItem<K extends JournalRecordType>(
    type: K,
    id: string,
  ): Promise<JournalItemLoad<JournalRecordTypes[K]>> {
    const unavailable = await this.#ensureAvailable();
    if (unavailable !== undefined) return { kind: "unavailable", error: unavailable };

    try {
      const load = await this.#loadRecord(type, id);
      if (load.kind === "loaded") return { kind: "loaded", value: load.value };
      if (load.kind === "recovery") {
        // A single-item load returns `unavailable`, never `missing` (F-JP-19):
        // the record exists, it just cannot be trusted to decode.
        return { kind: "unavailable", error: "recoveryRequired" };
      }
      return (await this.#inRecovery(type, id))
        ? { kind: "unavailable", error: "recoveryRequired" }
        : { kind: "missing" };
    } catch (error) {
      return { kind: "unavailable", error: errorCodeOf(error) };
    }
  }

  /** Reads one live record, quarantining it if it will not decode. */
  async #loadRecord<K extends JournalRecordType>(
    type: K,
    id: string,
  ): Promise<RecordLoad<JournalRecordTypes[K]>> {
    const path = journalRecordPath(type, id);
    const filenameId = id.toLowerCase();
    const read = await this.#files.read(path);

    if (read.kind === "missing") return { kind: "missing" };
    if (read.kind === "failed") {
      // Bytes that would not read are preserved exactly where they are.
      const item = await this.#recoveryItem(
        type,
        path,
        new Uint8Array(0),
        { kind: "unreadable" },
        filenameId,
      );
      return { kind: "recovery", item };
    }

    const decoded = decodeJournalRecordBytes(type, read.bytes, filenameId);
    if (decoded.kind === "decoded") return { kind: "loaded", value: decoded.value };

    const { reason, identity } = decoded.failure;
    const preserved = await quarantineJournalRecord(
      this.#files,
      type,
      filenameId,
      path,
      read.bytes,
      journalRecordIsMovable(reason),
    );
    const item = await this.#recoveryItem(
      type,
      preserved.path,
      preserved.bytes,
      reason,
      identity,
    );
    return { kind: "recovery", item };
  }

  /** Whether a preserved copy of this id is sitting in the quarantine. */
  async #inRecovery(type: JournalRecordType, id: string): Promise<boolean> {
    try {
      return await this.#files.fileExists(journalQuarantineRecordPath(type, id));
    } catch {
      // If we cannot tell, we must assume the worst: a write that proceeded on
      // an unanswered question is a write that may destroy the answer.
      return true;
    }
  }

  /**
   * Walks a library: every live record, then every preserved one.
   *
   * The quarantine is re-decoded rather than tracked in a side file, so a
   * recovery row is derived from the bytes it points at and cannot drift from
   * them. A preserved record that now decodes is left alone rather than
   * reinstated — the live path may hold something newer, and putting this one
   * back would overwrite a record the user has since written.
   */
  async #scan<K extends JournalRecordType>(
    type: K,
  ): Promise<ScanResult<JournalRecordTypes[K]>> {
    const items: JournalRecordTypes[K][] = [];
    const recoveries: JournalRecoveryItem[] = [];

    for (const name of await this.#files.list(journalLiveDirectory(type))) {
      const id = journalRecordIdFromFileName(name);
      if (id === undefined) continue;
      const load = await this.#loadRecord(type, id);
      if (load.kind === "loaded") items.push(load.value);
      else if (load.kind === "recovery") recoveries.push(load.item);
    }

    // The live pass moves what it quarantines, so a record it just preserved is
    // now sitting in the directory the next loop walks. Reporting it twice
    // would put two rows in front of the user for one file, and the second row
    // would export the same bytes as the first.
    const reported = new Set(recoveries.map((item) => item.path));

    const quarantine = journalQuarantineDirectory(type);
    for (const name of await this.#files.list(quarantine)) {
      const id = journalRecordIdFromFileName(name);
      if (id === undefined) continue;
      const path = journalPathJoin(quarantine, name);
      if (reported.has(path)) continue;
      const read = await this.#files.read(path);
      if (read.kind === "missing") continue;
      if (read.kind === "failed") {
        recoveries.push(
          await this.#recoveryItem(type, path, new Uint8Array(0), { kind: "unreadable" }, id),
        );
        continue;
      }
      const decoded = decodeJournalRecordBytes(type, read.bytes, id);
      if (decoded.kind === "decoded") continue;
      recoveries.push(
        await this.#recoveryItem(
          type,
          path,
          read.bytes,
          decoded.failure.reason,
          decoded.failure.identity,
        ),
      );
    }

    return { items, recoveries };
  }

  async #recoveryItem(
    type: JournalRecordType,
    path: string,
    bytes: Uint8Array,
    reason: JournalRecoveryItem["reason"],
    identity: string | undefined,
  ): Promise<JournalRecoveryItem> {
    const contentSHA256 = await this.#hash(bytes);
    return {
      // Identity chain: what the record claims, then what its filename claims,
      // then the digest. The spec's last resort is a fresh uuid; a digest is
      // used instead because a random id would give the same preserved file a
      // different row title on every scan, and this row is how a user finds it.
      id: identity ?? contentSHA256.slice(0, 32),
      kind: type,
      reason,
      path,
      byteCount: bytes.byteLength,
      contentSHA256,
    };
  }

  async #hash(bytes: Uint8Array): Promise<Sha256Hex> {
    return this.#digest.sha256(journalDigestChunks(bytes));
  }

  /* --- internals: writing ------------------------------------------------ */

  /**
   * The state a write needs to know about the record it is replacing.
   *
   * `recovery` and `failed` are separate because they lead to different
   * answers: a quarantined counterpart is `recoveryRequired` — a human has to
   * resolve it — while a substrate failure is whatever the substrate said.
   */
  async #loadForWrite<K extends JournalRecordType>(
    type: K,
    id: string,
  ): Promise<
    | { readonly kind: "value"; readonly value: JournalRecordTypes[K] | undefined }
    | { readonly kind: "recovery" }
    | { readonly kind: "failed"; readonly error: JournalRepositoryErrorCode }
  > {
    if (await this.#inRecovery(type, id)) return { kind: "recovery" };
    try {
      const load = await this.#loadRecord(type, id);
      if (load.kind === "recovery") return { kind: "recovery" };
      return { kind: "value", value: load.kind === "loaded" ? load.value : undefined };
    } catch (error) {
      return { kind: "failed", error: errorCodeOf(error) };
    }
  }

  /**
   * Writes the record, then reads it back and decodes it again.
   *
   * The value returned is the *re-decoded* one. Handing back the caller's own
   * object would let the app display an entry it merely hoped it had written
   * (F-JP-20), and comparing the re-encoded bytes catches the failures a
   * read-back of raw bytes cannot: a payload this build can no longer read, or
   * a decoder that quietly dropped a field.
   */
  async #writeRecord<K extends JournalRecordType>(
    type: K,
    id: string,
    value: JournalRecordTypes[K],
  ): Promise<JournalWriteResult<JournalRecordTypes[K]>> {
    const path = journalRecordPath(type, id);
    const bytes = encodeJournalRecord(value);

    try {
      await journalVerifiedWrite(this.#files, path, bytes, this.#token());
    } catch (error) {
      return { kind: "failed", error: errorCodeOf(error) };
    }

    const read = await this.#files.read(path);
    if (read.kind !== "bytes") return { kind: "failed", error: "writeNotVerified" };
    const decoded = decodeJournalRecordBytes(type, read.bytes, id.toLowerCase());
    if (decoded.kind !== "decoded") return { kind: "failed", error: "writeNotVerified" };
    if (canonicalJson(decoded.value) !== canonicalJson(value)) {
      return { kind: "failed", error: "writeNotVerified" };
    }
    return { kind: "written", value: decoded.value };
  }

  async #runDeletion(targets: readonly JournalDeletionRequest[]): Promise<JournalDeletionResult> {
    return runJournalDeletion(this.#files, this.#token(), targets);
  }

  /**
   * A staging name segment unique to this operation.
   *
   * The sequence is what makes it unique: two writes inside one millisecond
   * would otherwise share a backup path, and the second would overwrite the
   * first's only copy of the record it is replacing.
   */
  #token(): string {
    this.#sequence += 1;
    const stamp = Math.trunc(this.#now());
    return `${Number.isFinite(stamp) ? stamp : 0}-${this.#sequence}`.replace(/[^0-9a-zA-Z-]/g, "-");
  }
}

/* ------------------------------------------------------------------------- *
 * Revision discipline (F-JP-24)
 * ------------------------------------------------------------------------- */

function revisionConflict(
  existing: JournalEntry | undefined,
  candidate: JournalEntry,
): JournalRepositoryErrorCode | undefined {
  if (existing === undefined) {
    // A first write claiming any other revision is a client that thinks it is
    // editing something; there is nothing here to have edited.
    return candidate.revision === 1 ? undefined : "revisionConflict";
  }
  if (existing.revision >= JOURNAL_REVISION_MAX) return "revisionOverflow";
  if (candidate.revision !== existing.revision + 1) return "revisionConflict";

  if (
    candidate.createdAt !== existing.createdAt ||
    canonicalJson(candidate.effort ?? null) !== canonicalJson(existing.effort ?? null) ||
    canonicalJson(candidate.migration ?? null) !== canonicalJson(existing.migration ?? null)
  ) {
    // `createdAt`, `effort` and `migration` are frozen across revisions: an
    // entry that never had a workout can never acquire one, and a migrated
    // entry can never stop being the record of that legacy note.
    return "immutableContextChanged";
  }
  if (candidate.updatedAt < existing.updatedAt) return "immutableContextChanged";
  return undefined;
}
